/**
 * iter696 — Final extraction from PrintersSettingsPage.jsx.
 *
 * Main printer list: filter bar (search + role chips + branch dropdown)
 * + table of rows + per-row actions (ping, test-print, ePOS-direct test,
 * edit, delete) + empty state.
 *
 * State strategy (final of 4):
 *   Filter state (search / roleFilter / branchFilter) and the derived
 *   `filtered` memo live INSIDE this component — they're only relevant
 *   here. Constants `TYPE_META` and `ROLE_FILTERS` and the
 *   `branchName` lookup are also internal.
 *
 *   The parent keeps ownership of `printers / branches / loading`
 *   (server-fetched) and all in-flight action states (`pingResults`,
 *   `pinging`, `testing`, `eposRowTesting`) because the row-action
 *   handlers it owns flip them.
 *
 * Props (13): a clean read-vs-callback boundary.
 */
import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Plus, Loader2, Printer, Wifi, Edit, Trash2,
  Receipt as ReceiptIcon, ChefHat, GlassWater, Tag as TagIcon,
  CheckCircle2, XCircle, Zap, Languages,
} from 'lucide-react';

const TYPE_META = {
  receipt: { label: 'Cashier',  icon: ReceiptIcon, color: 'bg-violet-100 text-violet-700 border-violet-200' },
  kitchen: { label: 'Kitchen',  icon: ChefHat,     color: 'bg-orange-100 text-orange-700 border-orange-200' },
  bar:     { label: 'Bar',      icon: GlassWater,  color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  label:   { label: 'Label',    icon: TagIcon,     color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const ROLE_FILTERS = ['all', 'receipt', 'kitchen', 'bar', 'label'];

/** Compact "last used" / "last tested" stamp helper. Returns
 *  human-friendly relative strings like "2m ago", "3h ago", "yesterday". */
function relTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diff = Math.max(0, Date.now() - d.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return 'yesterday';
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function PrinterListView({
  printers,
  branches,
  loading,
  pingResults,
  pinging,
  testing,
  eposRowTesting,
  bilingualTesting,
  bilingualTrends,
  onAdd,
  onEdit,
  onDelete,
  onPing,
  onTestPrint,
  onTestEposDirect,
  onBilingualTest,
}) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');

  const branchName = (id) => {
    if (!id) return 'All branches';
    return branches.find(b => b.id === id)?.name || '(branch removed)';
  };

  const filtered = useMemo(() => {
    return printers.filter(p => {
      if (roleFilter !== 'all' && p.type !== roleFilter) return false;
      if (branchFilter !== 'all') {
        const want = branchFilter === '__shared__' ? null : branchFilter;
        if ((p.branch_id || null) !== want) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${p.name || ''} ${p.ip_address || ''} ${p.station || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [printers, roleFilter, branchFilter, search]);

  return (
    <>
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, IP, station…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 w-64 rounded-lg"
            data-testid="printers-search-input"
          />
        </div>
        <div className="flex items-center gap-1 ml-1">
          {ROLE_FILTERS.map(r => (
            <Button
              key={r}
              size="sm"
              variant={roleFilter === r ? 'default' : 'outline'}
              className={`h-8 rounded-full text-xs px-3 capitalize ${roleFilter === r ? 'bg-foreground text-white' : ''}`}
              onClick={() => setRoleFilter(r)}
              data-testid={`role-filter-${r}`}
            >
              {r === 'all' ? 'All roles' : TYPE_META[r]?.label || r}
            </Button>
          ))}
        </div>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger className="h-9 w-44 rounded-lg ml-1" data-testid="branch-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            <SelectItem value="__shared__">Shared (no branch)</SelectItem>
            {branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-white/40 bg-white/70 dark:bg-muted/70 backdrop-blur-xl shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] overflow-hidden transition-shadow hover:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12)]" data-testid="printers-list">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">
            <Loader2 className="animate-spin mx-auto" size={20} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="printers-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-orange-50 mx-auto flex items-center justify-center mb-3">
              <Printer size={26} className="text-orange-500" />
            </div>
            <h3 className="font-semibold text-base">
              {printers.length === 0 ? 'No printers configured yet' : 'No printers match this filter'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              {printers.length === 0
                ? 'Add your first thermal printer to start auto-printing receipts and kitchen tickets at this branch.'
                : 'Try changing the role or branch filter, or clear the search.'}
            </p>
            {printers.length === 0 && (
              <Button className="mt-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" size="sm" onClick={onAdd} data-testid="empty-add-printer-btn">
                <Plus size={14} className="mr-1.5" />Add new printer
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {/* Header row (desktop) */}
            <div className="hidden md:grid grid-cols-[1fr_140px_180px_120px_100px_180px] gap-3 px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground bg-muted/40">
              <span>Printer</span>
              <span>Role</span>
              <span>Branch</span>
              <span>Network</span>
              <span>Mode</span>
              <span className="text-right">Actions</span>
            </div>
            {filtered.map(p => {
              const meta = TYPE_META[p.type] || TYPE_META.receipt;
              const Icon = meta.icon;
              const pingState = pingResults[p.id];
              return (
                <div
                  key={p.id}
                  className="grid grid-cols-1 md:grid-cols-[1fr_140px_180px_120px_100px_180px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors"
                  data-testid={`printer-row-${p.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${meta.color}`}>
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate" data-testid={`printer-name-${p.id}`}>{p.name}</span>
                        {p.is_default && (
                          <Badge variant="outline" className="text-[9px] h-5 border-primary/30 text-primary">Default</Badge>
                        )}
                        {p.airprint_enabled && (
                          <Badge variant="outline" className="text-[9px] h-5 border-sky-300 bg-sky-50 text-sky-700" title="iPads can print directly to this printer using Safari's native print dialog — no Bridge required">
                            🍎 AirPrint
                          </Badge>
                        )}
                        {p.auto_print && (
                          <Badge variant="outline" className="text-[9px] h-5 border-emerald-300 text-emerald-700">Auto-print</Badge>
                        )}
                        {pingState === 'ok' && (
                          <Badge variant="outline" className="text-[9px] h-5 border-emerald-300 text-emerald-700 flex items-center gap-1"><CheckCircle2 size={9}/>Online</Badge>
                        )}
                        {pingState === 'fail' && (
                          <Badge variant="outline" className="text-[9px] h-5 border-rose-300 text-rose-700 flex items-center gap-1"><XCircle size={9}/>Offline</Badge>
                        )}
                      </div>
                      {p.station && (
                        <p className="text-[11px] text-muted-foreground truncate">Station: {p.station}</p>
                      )}
                      {(p.last_used_at || p.last_tested_at) && (
                        <p className="text-[10px] text-muted-foreground/80 truncate" data-testid={`printer-stamps-${p.id}`}>
                          {p.last_used_at && <span title={`Last printed at ${p.last_used_at}`}>Used {relTime(p.last_used_at)}</span>}
                          {p.last_used_at && p.last_tested_at && <span className="mx-1">·</span>}
                          {p.last_tested_at && <span title={`Last tested at ${p.last_tested_at}`}>Tested {relTime(p.last_tested_at)}</span>}
                        </p>
                      )}
                      {/* iter917 — Bilingual test trend strip: up to 5
                          colored dots (green ok / red failed) with a
                          hover tooltip showing when the test fired. */}
                      {p.ip_address && Array.isArray(bilingualTrends?.[p.id]) && bilingualTrends[p.id].length > 0 && (
                        <div className="flex items-center gap-1 mt-1" data-testid={`bilingual-trend-${p.id}`}>
                          <span className="text-[9px] text-muted-foreground/70">AR/EN trend:</span>
                          {bilingualTrends[p.id].slice(0, 5).map((e, i) => (
                            <span
                              key={e.id || i}
                              className={`inline-block w-1.5 h-1.5 rounded-full ${e.ok ? 'bg-emerald-500' : 'bg-rose-500'}`}
                              title={`${e.ok ? 'OK' : 'FAILED'} · ${e.created_at ? new Date(e.created_at).toLocaleString() : ''}${e.msg ? ' · ' + e.msg : ''}`}
                              data-testid={`bilingual-trend-dot-${p.id}-${i}`}
                            />
                          ))}
                          <span className="text-[9px] text-muted-foreground/60 ml-0.5" data-testid={`bilingual-trend-count-${p.id}`}>
                            ({bilingualTrends[p.id].filter((e) => e.ok).length}/{bilingualTrends[p.id].length})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs"><Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge></div>
                  <div className="text-xs text-muted-foreground truncate">{branchName(p.branch_id)}</div>
                  <div className="text-xs font-mono text-muted-foreground truncate">
                    {p.ip_address || '—'}{p.port ? `:${p.port}` : ''}
                    {p.epos_device_id && (
                      <span className="ml-1 text-[9px] text-emerald-700" title={`ePOS-Print Device ID: ${p.epos_device_id}`}>
                        · devid={p.epos_device_id}
                      </span>
                    )}
                  </div>
                  <div className="text-xs capitalize text-muted-foreground">{p.print_mode || 'auto'}</div>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" disabled={pinging === p.id} onClick={() => onPing(p)} title="Ping" data-testid={`ping-${p.id}`}>
                      {pinging === p.id ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" disabled={testing === p.id} onClick={() => onTestPrint(p)} title="Test print (via Bridge)" data-testid={`test-${p.id}`}>
                      {testing === p.id ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                    </Button>
                    {/* iter679 — ePOS-Print Direct test per printer. Bypasses
                        the Print Bridge, fires the XML payload straight at
                        the printer over HTTPS. Critical for verifying the
                        per-printer epos_device_id setting is correct. */}
                    {p.ip_address && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-lg text-emerald-700 hover:bg-emerald-50"
                        disabled={eposRowTesting === p.id}
                        onClick={() => onTestEposDirect(p)}
                        title={`ePOS Direct test (HTTPS → ${p.ip_address}, devid=${p.epos_device_id || 'local_printer'})`}
                        data-testid={`epos-test-${p.id}`}
                      >
                        {eposRowTesting === p.id ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                      </Button>
                    )}
                    {/* iter915 — On-Printer Preview: fire a bilingual (EN + AR)
                        test receipt through the same ePOS Direct raster path
                        used by real orders. Confirms Arabic prints correctly
                        + the logo lands after the iter915 Data-URI + resolution
                        downscale hardening. */}
                    {p.ip_address && onBilingualTest && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-lg text-sky-700 hover:bg-sky-50"
                        disabled={bilingualTesting === p.id}
                        onClick={() => onBilingualTest(p)}
                        title="Send bilingual (EN + AR) test receipt"
                        data-testid={`bilingual-test-${p.id}`}
                      >
                        {bilingualTesting === p.id ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => onEdit(p)} title="Edit" data-testid={`edit-${p.id}`}>
                      <Edit size={13} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-rose-500 hover:bg-rose-50" onClick={() => onDelete(p)} title="Delete" data-testid={`delete-${p.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
